import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import {
  MetadataList,
  MetadataListRow,
} from '@texas/components/shared/MetadataList';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { setValueAsDecimalExtender } from '../../sales/shared';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { formatRange } from '@texas/components/vdc/utils';
import { ReactSelectOption } from '@texas/types';
import { templateRangeTiersApi } from '@texas/api/endpoints/metadata/templateRangeTiersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useEffect } from 'react';
import { PreviewTemplate } from '@texas/components/vdc/PreviewTemplate';
import { PreviewPane } from '@texas/components/vdc/PreviewPanel';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { request } from '@texas/utils/helpers/httpHelpers';
import {
  AddVdcOptionRequest,
  articlesApi,
} from '@texas/api/endpoints/articlesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { reactEvents } from '@bridge/reactEvents';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { treatmentsApi } from '@texas/api/endpoints/metadata/treatmentsApi';

interface Props extends SharedDisclosureProps {
  variantId: number;
}

export function SelectVdcTemplate({ isOpen, onClose, variantId }: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('vdc.addVdcTemplate')}</ModalHeader>
        <ModalBody>
          <Inner variantId={variantId} onClose={onClose} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function Inner({
  variantId,
  onClose,
}: {
  variantId: number;
  onClose: () => void;
}) {
  const {
    register,
    control,
    formState: { errors, isValid, isSubmitting },
    watch,
    handleSubmit,
  } = useForm<AddVdcOptionRequest>({ defaultValues: { variantId } });
  const { t } = useTranslation();
  const templateCompositionGroupId = watch('templateCompositionGroupId');

  const {
    data: templateRangeTiers,
    refetch: refetchTemplateRangeTiers,
    loading: loadingTemplateRangeTiers,
  } = useApiResource(templateRangeTiersApi.getAll);
  const {
    data: treatments,
    refetch: refetchTreatments,
    loading: loadingTreatments,
  } = useApiResource(treatmentsApi.getAll);

  const {
    data: templates,
    refetch: refetchTemplates,
    loading: loadingTemplates,
  } = useApiResource(compositionGroupApi.templatesOverview);
  const {
    data: variantInfo,
    refetch: refetchVariantInfo,
    loading: loadingVariantInfo,
  } = useApiResource(variantsApi.getVariantInfo);

  const { request: submitRequest, error: submitError } = useApiRequest(
    articlesApi.addVdcArticleOption,
  );

  useEffect(() => {
    refetchTemplateRangeTiers({
      searchTerm: '',
      includeArchived: false,
    });
    refetchTreatments({
      sortBy: '',
      sortDesc: false,
      searchTerm: '',
      includeArchived: false,
      vdcSupport: true,
      preciseIdFiltering: false,
      productGroupId: null,
    });
    refetchTemplates({ isReady: true });
    refetchVariantInfo(variantId);
  }, [
    refetchTemplateRangeTiers,
    refetchTreatments,
    refetchTemplates,
    refetchVariantInfo,
    variantId,
  ]);

  const submitForm = async (data: AddVdcOptionRequest) => {
    await request(submitRequest, [data], (_) => {
      compositionGroupEvents.onCreate.dispatch();
      reactEvents.newOptionAddedWithVariant.dispatch();
      onClose();
    });
  };

  return (
    <Box>
      {submitError && <ErrorDetails error={submitError} />}
      <MetadataList>
        <MetadataListRow label="Article product group">
          {variantInfo?.productGroupName}
        </MetadataListRow>
        <MetadataListRow label="Branches">
          {variantInfo?.branches.map((b) => b.name).join(', ')}
        </MetadataListRow>
      </MetadataList>
      <Divider my={4} />
      <form onSubmit={handleSubmit(submitForm)}>
        <Flex flexDir="column" gap={2}>
          <FormControl isInvalid={!!errors.width}>
            <TexasFormLabel>{t('vdc.labelWidth')}</TexasFormLabel>
            <Input
              {...register('width', {
                required: true,
                setValueAs: (value) => setValueAsDecimalExtender(value, false),
              })}
              variant="texas"
            />
            <FormErrorMessage>{errors.width?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.length}>
            <TexasFormLabel>{t('vdc.labelLength')}</TexasFormLabel>
            <Input
              {...register('length', {
                required: true,
                setValueAs: (value) => setValueAsDecimalExtender(value, false),
              })}
              variant="texas"
            />
            <FormErrorMessage>{errors.length?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.tierRangeId}>
            <TexasFormLabel>{t('vdc.estimatedAnnualQuantity')}</TexasFormLabel>
            <Controller
              name="tierRangeId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const selectedRange = templateRangeTiers?.find(
                  (p) => p.id === field.value,
                );
                return (
                  <TexasSelect
                    {...field}
                    isLoading={loadingTemplateRangeTiers}
                    isClearable={true}
                    value={{
                      label: selectedRange
                        ? formatRange(
                            selectedRange.startRange,
                            selectedRange.endRange,
                          )
                        : '',
                      value: field.value,
                    }}
                    onChange={(e) => field.onChange(e?.value)}
                    options={
                      templateRangeTiers?.map<ReactSelectOption>((x) => ({
                        value: x.id,
                        label: formatRange(x.startRange, x.endRange),
                      })) ?? []
                    }
                  />
                );
              }}
            />
            <FormErrorMessage>{errors.tierRangeId?.message}</FormErrorMessage>
          </FormControl>
          <Flex flexDir="column" gap={2}>
            <FormControl isInvalid={!!errors.templateCompositionGroupId}>
              <TexasFormLabel>{t('vdc.vdcTemplate')}</TexasFormLabel>
              <Controller
                name="templateCompositionGroupId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  const template = templates?.find((p) => p.id === field.value);
                  return (
                    <TexasSelect
                      {...field}
                      isLoading={loadingTemplates}
                      isClearable={true}
                      value={{
                        label: template
                          ? !template.note || template.note.length === 0
                            ? t('compositionGroup.option.optionNr', {
                                nr: template.version,
                              })
                            : template.note
                          : '',
                        value: field.value,
                      }}
                      onChange={(e) => field.onChange(e?.value)}
                      options={
                        templates?.map<ReactSelectOption>((x) => ({
                          value: x.id,
                          label:
                            !x.note || x.note.length === 0
                              ? t('compositionGroup.option.optionNr', {
                                  nr: x.version,
                                })
                              : x.note,
                        })) ?? []
                      }
                    />
                  );
                }}
              />
              <FormErrorMessage>
                {errors.templateCompositionGroupId?.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl>
              <TexasFormLabel>{t('configuration.treatments')}</TexasFormLabel>
              <Controller
                name="treatmentIds"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    isMulti={true}
                    isLoading={loadingTreatments}
                    isClearable={true}
                    value={field.value?.map<ReactSelectOption>((x) => ({
                      label: treatments?.find((p) => p.id === x)?.name ?? '',
                      value: x,
                    }))}
                    onChange={(e) => field.onChange(e.map((x) => x.value))}
                    options={
                      treatments?.map<ReactSelectOption>((x) => ({
                        value: x.id,
                        label: x.name,
                      })) ?? []
                    }
                    closeMenuOnSelect={false}
                  />
                )}
              />
            </FormControl>
          </Flex>
          {templateCompositionGroupId && (
            <PreviewTemplate compositionGroupId={templateCompositionGroupId} />
          )}
          {loadingVariantInfo && (
            <SpinnerWithLabel label={t('vdc.loadingVdcPricing')} />
          )}
          {variantInfo?.productGroupId && (
            <PreviewPane
              disabled={!isValid || isSubmitting}
              formValues={{
                productGroupId: variantInfo.productGroupId,
                width: watch('width'),
                length: watch('length'),
                tierRangeId: watch('tierRangeId'),
                compositionGroupId: watch('templateCompositionGroupId'),
                treatmentIds: watch('treatmentIds'),
              }}
              selectedBranches={variantInfo.branches.map((b) => b.id)}
            />
          )}
          <SubmitButton
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
          >
            {t('compositionGroup.option.addNew')}
          </SubmitButton>
        </Flex>
      </form>
    </Box>
  );
}
